// The at-rest `enc:` envelope grammar (ADR-0061 Part 1 / 1b).
//
//   v1 (legacy reads):  enc:<base64(iv)>:<base64(ct+tag)>                      # 2 parts → implicit scheme a
//   v2 (all schemes):   enc:2:<base64url(header)>:<base64(iv)>:<base64(ct+tag)>
//
// The v2 header is canonical CBOR (see envelope-cbor.ts) so a given logical
// header always serialises to the same bytes — which lets those exact bytes be
// authenticated as part of the GCM AAD (Part 1b). The header is
// scheme-discriminated; only scheme `a` (app-keyed) is READ end-to-end in this
// increment. Schemes `k`/`c` (KEK-wrapped DEK) are parsed and dispatched here but
// resolved by ADR-0062.
//
// This module is pure grammar + AAD construction — no key material, no cipher.
// Key resolution and the cipher live in keyring.ts / secret.ts.

import { createHash } from 'node:crypto';

import { decodeCanonicalCbor, encodeCanonicalCbor, type CborValue } from './envelope-cbor.js';
import { MalformedEnvelopeError, UnknownSchemeError } from './secret-errors.js';

export const PREFIX = 'enc:';
/** The literal that occupies the first colon-part of a v2 envelope. */
export const V2_MARKER = '2';

const IV_LENGTH = 12;
const AUTH_TAG_LENGTH = 16;

// ---------------------------------------------------------------------------
// Header shapes
// ---------------------------------------------------------------------------

/** Scheme `a`: app-keyed, no DEK. `kid` selects the app key that sealed it. */
export type SchemeAHeader = { s: 'a'; kid: string };
/** One wrapped-DEK entry: the DEK ciphertext produced by KEK `ref`. */
export type WrapEntry = { ref: string; dek: string };
/** Scheme `k`: platform-CMK-wrapped DEK (ADR-0062 increment C). */
export type SchemeKHeader = { s: 'k'; w: WrapEntry[] };
/** Scheme `c`: customer-CMK-wrapped DEK; `w` may dual-wrap (ADR-0062 increment D). */
export type SchemeCHeader = { s: 'c'; w: WrapEntry[] };
export type EnvelopeHeader = SchemeAHeader | SchemeKHeader | SchemeCHeader;

const KID_RE = /^[0-9a-f]{8}$/;

/**
 * The non-secret key id: the first 8 hex of SHA-256(key). Deterministic, changes
 * iff the key changes, and never reveals the key material.
 */
export function deriveKid(key: Buffer): string {
  return createHash('sha256').update(key).digest('hex').slice(0, 8);
}

// ---------------------------------------------------------------------------
// Header encode / decode
// ---------------------------------------------------------------------------

/** Canonically encode a header to its authenticated byte form. */
export function encodeHeader(header: EnvelopeHeader): Buffer {
  return encodeCanonicalCbor(header as unknown as CborValue);
}

function isPlainRecord(v: CborValue): v is { [k: string]: CborValue } {
  return typeof v === 'object' && v !== null && !Array.isArray(v);
}

function decodeWrapList(w: CborValue): WrapEntry[] {
  if (!Array.isArray(w) || w.length === 0) {
    throw new MalformedEnvelopeError('envelope: scheme k/c header requires a non-empty wrap list');
  }
  return w.map((entry) => {
    if (!isPlainRecord(entry)) {
      throw new MalformedEnvelopeError('envelope: wrap entry must be a map');
    }
    const keys = Object.keys(entry).sort();
    if (keys.length !== 2 || keys[0] !== 'dek' || keys[1] !== 'ref') {
      throw new MalformedEnvelopeError('envelope: wrap entry must have exactly {ref, dek}');
    }
    if (typeof entry.ref !== 'string' || typeof entry.dek !== 'string') {
      throw new MalformedEnvelopeError('envelope: wrap entry ref/dek must be strings');
    }
    return { ref: entry.ref, dek: entry.dek };
  });
}

/**
 * Decode + validate a header from its canonical bytes. Throws
 * `MalformedEnvelopeError` for a structurally-wrong header and
 * `UnknownSchemeError` for a scheme this build does not recognise.
 */
export function decodeHeader(bytes: Buffer): EnvelopeHeader {
  let decoded: CborValue;
  try {
    decoded = decodeCanonicalCbor(bytes);
  } catch (cause) {
    throw new MalformedEnvelopeError('envelope: header is not canonical CBOR', { cause });
  }
  if (!isPlainRecord(decoded) || typeof decoded.s !== 'string') {
    throw new MalformedEnvelopeError('envelope: header must be a map with a string `s`');
  }

  const keys = Object.keys(decoded).sort();

  if (decoded.s === 'a') {
    if (keys.length !== 2 || keys[0] !== 'kid' || keys[1] !== 's') {
      throw new MalformedEnvelopeError('envelope: scheme a header must have exactly {s, kid}');
    }
    if (typeof decoded.kid !== 'string' || !KID_RE.test(decoded.kid)) {
      throw new MalformedEnvelopeError('envelope: scheme a kid must be 8 lowercase hex chars');
    }
    return { s: 'a', kid: decoded.kid };
  }

  if (decoded.s === 'k' || decoded.s === 'c') {
    if (keys.length !== 2 || keys[0] !== 's' || keys[1] !== 'w') {
      throw new MalformedEnvelopeError('envelope: scheme k/c header must have exactly {s, w}');
    }
    return { s: decoded.s, w: decodeWrapList(decoded.w!) };
  }

  throw new UnknownSchemeError(`envelope: unknown scheme ${JSON.stringify(decoded.s)}`);
}

// ---------------------------------------------------------------------------
// Envelope parse / format
// ---------------------------------------------------------------------------

export type ParsedV1 = { version: 1; iv: Buffer; data: Buffer };
export type ParsedV2 = {
  version: 2;
  header: EnvelopeHeader;
  /** The canonical header bytes exactly as stored — the AAD prefix. */
  headerBytes: Buffer;
  iv: Buffer;
  data: Buffer;
};
export type ParsedEnvelope = ParsedV1 | ParsedV2;

/**
 * Decode a base64 / base64url part and REJECT any non-canonical spelling by
 * requiring the decode to round-trip to the exact input. Without this, distinct
 * envelope strings can decode to identical bytes (base64 malleability) — and v1
 * blobs carry no AAD to catch it downstream — so the strictness is enforced at
 * the parse boundary, before CBOR parsing or AEAD authentication. The canonical
 * forms are exactly what `formatV1`/`formatV2` emit: padded standard base64 for
 * iv/ciphertext, unpadded base64url for the header.
 */
function decodeStrict(part: string, encoding: 'base64' | 'base64url', what: string): Buffer {
  const buf = Buffer.from(part, encoding);
  if (buf.length === 0) throw new MalformedEnvelopeError(`envelope: empty ${what}`);
  if (buf.toString(encoding) !== part) {
    throw new MalformedEnvelopeError(`envelope: non-canonical ${encoding} in ${what}`);
  }
  return buf;
}

function assertShape(iv: Buffer, data: Buffer): void {
  if (iv.length !== IV_LENGTH) {
    throw new MalformedEnvelopeError(`envelope: iv must be ${IV_LENGTH} bytes, got ${iv.length}`);
  }
  if (data.length < AUTH_TAG_LENGTH) {
    throw new MalformedEnvelopeError('envelope: ciphertext shorter than the auth tag');
  }
}

/**
 * Parse an `enc:` string into a discriminated v1/v2 structure. Throws
 * `MalformedEnvelopeError`/`UnknownSchemeError`; performs NO decryption.
 */
export function parseEnvelope(encoded: string): ParsedEnvelope {
  if (!encoded.startsWith(PREFIX)) {
    throw new MalformedEnvelopeError('envelope: missing enc: prefix');
  }
  const parts = encoded.slice(PREFIX.length).split(':');

  if (parts.length === 2) {
    const iv = decodeStrict(parts[0]!, 'base64', 'iv');
    const data = decodeStrict(parts[1]!, 'base64', 'ciphertext');
    assertShape(iv, data);
    return { version: 1, iv, data };
  }

  if (parts.length === 4 && parts[0] === V2_MARKER) {
    const headerBytes = decodeStrict(parts[1]!, 'base64url', 'header');
    const header = decodeHeader(headerBytes);
    const iv = decodeStrict(parts[2]!, 'base64', 'iv');
    const data = decodeStrict(parts[3]!, 'base64', 'ciphertext');
    assertShape(iv, data);
    return { version: 2, header, headerBytes, iv, data };
  }

  throw new MalformedEnvelopeError('envelope: unrecognised layout');
}

/** Build a v1 envelope string (`enc:<b64 iv>:<b64 ct+tag>`). */
export function formatV1(iv: Buffer, data: Buffer): string {
  return `${PREFIX}${iv.toString('base64')}:${data.toString('base64')}`;
}

/** Build a v2 envelope string from canonical header bytes + iv + ct+tag. */
export function formatV2(headerBytes: Buffer, iv: Buffer, data: Buffer): string {
  return `${PREFIX}${V2_MARKER}:${headerBytes.toString('base64url')}:${iv.toString(
    'base64',
  )}:${data.toString('base64')}`;
}

// ---------------------------------------------------------------------------
// AAD
// ---------------------------------------------------------------------------

/**
 * The non-secret context a v2 ciphertext is bound to, org- and field-grained
 * (ADR-0061 Part 1b). `<field>` is required, not decorative: `teams.settings` /
 * `organizations.settings` hold multiple secret fields in one JSONB column, so a
 * `(table,column)`-only AAD could not stop an intra-row field swap.
 */
export type DecryptContext = {
  orgId: string;
  table: string;
  column: string;
  field: string;
  rowId: string;
};

/** The canonical context string bound into the AAD. */
export function contextString(ctx: DecryptContext): string {
  return `org:${ctx.orgId}:${ctx.table}:${ctx.column}:${ctx.field}:${ctx.rowId}`;
}

/**
 * The `<field>` used in the AAD for a DIRECT ciphertext column — one whose
 * registry `field` is `null` because the column value IS the secret (a `text` /
 * `bytea` ciphertext column, not a JSONB leaf). The empty string is a canonical,
 * collision-free choice: a JSONB leaf key is never the empty string, so
 * `(table, column, '')` (direct column) can never alias `(table, column, 'api_key')`
 * (JSONB leaf) in the AAD.
 */
export const DIRECT_COLUMN_FIELD_SENTINEL = '';

/**
 * Build the org+field-grained {@link DecryptContext} for a registered secret
 * field. This is the single bridge between the registry coordinates
 * (`table`/`column`/`field`, where `field` may be `null` for a direct column) and
 * the AAD context the v2 envelope binds (`field` required). Every writer AND the
 * re-seal engine (ADR-0061 Part 3) MUST construct their context through this
 * function so both sides reconstruct byte-identical AAD — a divergence here is a
 * silent `AeadAuthFailureError` at decrypt time, not a compile error.
 *
 *   - `orgId`: the owning `organization_id`. For an `organizations` row this
 *     equals `rowId` (the org's own id); for a team-/agent-scoped row it is that
 *     row's `organization_id`, NOT its primary key.
 *   - `rowId`: the row's primary key value (e.g. `team_id` for `teams`).
 *
 * A `null` `field` (direct column) maps to {@link DIRECT_COLUMN_FIELD_SENTINEL}.
 */
export function buildDecryptContext(
  coords: { table: string; column: string; field: string | null },
  runtime: { orgId: string; rowId: string },
): DecryptContext {
  return {
    orgId: runtime.orgId,
    table: coords.table,
    column: coords.column,
    field: coords.field ?? DIRECT_COLUMN_FIELD_SENTINEL,
    rowId: runtime.rowId,
  };
}

/**
 * AAD = canonical_header_bytes || "org:<org>:<table>:<column>:<field>:<row>".
 * Binding the header bytes (not just the context) means a storage-level swap of
 * the scheme/kid/wrap-list causes a GCM auth failure instead of silently
 * redirecting the decrypt to a different key/custody path.
 */
export function buildAad(headerBytes: Buffer, ctx: DecryptContext): Buffer {
  return Buffer.concat([headerBytes, Buffer.from(contextString(ctx), 'utf8')]);
}
